import { useState, useEffect } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  Briefcase, Plus, Loader2, AlertCircle, ChevronRight, Calendar, Hash, Search, Filter,
} from "lucide-react";

interface Prestation {
  id: string; numero: string; statut: string; typePrestation: string; titre: string;
  clientFinalNom: string | null; modeleRobot: string | null; siteNom: string | null;
  niveauUrgence: string | null; dateIntervention: string | null; createdAt: string;
}

const STATUT_LABELS: Record<string, string> = {
  demande_recue: "Demande reçue", devis_envoye: "Devis envoyé", devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé", intervention_planifiee: "Planifiée", intervention_realisee: "Réalisée",
  facture: "Facturée", clos: "Clôturée", annule: "Annulée",
};
const STATUT_COLORS: Record<string, string> = {
  demande_recue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  devis_envoye: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  devis_accepte: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  devis_refuse: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  intervention_planifiee: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  intervention_realisee: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  facture: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  clos: "bg-secondary text-muted-foreground", annule: "bg-secondary text-muted-foreground",
};
const URGENCE_COLORS: Record<string, string> = {
  urgent: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400",
  critique: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
};

const EN_COURS_STATUTS = new Set(["demande_recue", "devis_envoye", "devis_accepte", "intervention_planifiee"]);
const HISTORIQUE_STATUTS = new Set(["devis_refuse", "intervention_realisee", "facture", "clos", "annule"]);

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

type TabKey = "en_cours" | "historique" | "toutes";

export default function PrestationsPage() {
  const { brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [prestations, setPrestations] = useState<Prestation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab
  const [tab, setTab] = useState<TabKey>("en_cours");

  // Filters
  const [search, setSearch] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [filterRobot, setFilterRobot] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  async function load() {
    setLoading(true); setError(null);
    try {
      const data = await authFetch("/api/portail/prestations").then(r => r.json());
      setPrestations(Array.isArray(data) ? data : []);
    } catch { setError("Erreur lors du chargement."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function applyFilters(list: Prestation[]): Prestation[] {
    return list.filter(p => {
      if (tab === "en_cours" && !EN_COURS_STATUTS.has(p.statut)) return false;
      if (tab === "historique" && !HISTORIQUE_STATUTS.has(p.statut)) return false;
      if (search && !`${p.titre} ${p.numero} ${p.clientFinalNom || ""} ${p.modeleRobot || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterSite && !(p.siteNom || "").toLowerCase().includes(filterSite.toLowerCase())) return false;
      if (filterRobot && !(p.modeleRobot || "").toLowerCase().includes(filterRobot.toLowerCase())) return false;
      if (filterDateFrom && p.createdAt < filterDateFrom) return false;
      if (filterDateTo && p.createdAt > filterDateTo + "T23:59:59") return false;
      return true;
    });
  }

  const filtered = prestations ? applyFilters(prestations) : [];
  const enCoursCount = prestations ? prestations.filter(p => EN_COURS_STATUTS.has(p.statut)).length : 0;
  const hasActiveFilters = !!(filterSite || filterRobot || filterDateFrom || filterDateTo);

  return (
    <div className="p-6 max-w-5xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Briefcase className="w-6 h-6" /> Sous-traitance
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Demandez des prestations d'intervention à Jobotto.</p>
        </div>
        <button onClick={() => navigate("/prestations/nouvelle")}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: primary }} data-testid="button-new-prestation">
          <Plus className="w-4 h-4" /> Nouvelle demande
        </button>
      </div>

      {/* Tabs En cours / Historique / Toutes */}
      <div className="flex border-b border-border/60 gap-0">
        {([
          { key: "en_cours", label: "En cours", count: enCoursCount },
          { key: "historique", label: "Historique", count: null },
          { key: "toutes", label: "Toutes", count: prestations?.length ?? null },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-5 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
            {t.count !== null && <span className="text-xs bg-secondary text-muted-foreground px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input className="w-full pl-8 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} data-testid="input-search" />
        </div>
        <button onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${hasActiveFilters ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}
          data-testid="btn-filters">
          <Filter className="w-4 h-4" /> Filtres{hasActiveFilters && " ✦"}
        </button>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="bg-secondary/30 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border border-border/40">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Site</label>
            <input className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Nom du site" value={filterSite} onChange={e => setFilterSite(e.target.value)} data-testid="filter-site" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Robot</label>
            <input className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Modèle robot" value={filterRobot} onChange={e => setFilterRobot(e.target.value)} data-testid="filter-robot" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Date (début)</label>
            <input type="date" className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} data-testid="filter-date-from" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Date (fin)</label>
            <input type="date" className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} data-testid="filter-date-to" />
          </div>
          {hasActiveFilters && (
            <div className="col-span-full flex justify-end">
              <button onClick={() => { setFilterSite(""); setFilterRobot(""); setFilterDateFrom(""); setFilterDateTo(""); }}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors">Effacer les filtres</button>
            </div>
          )}
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>}
      {error && <div className="flex items-center gap-2 text-destructive text-sm p-4 bg-destructive/10 rounded-xl"><AlertCircle className="w-4 h-4" /> {error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/30" />
          <p className="text-muted-foreground">{tab === "en_cours" ? "Aucune demande en cours." : tab === "historique" ? "Aucune prestation terminée." : "Aucune prestation."}</p>
          {tab === "en_cours" && (
            <button onClick={() => navigate("/prestations/nouvelle")} className="text-sm font-medium hover:opacity-80 transition" style={{ color: primary }}>
              Créer votre première demande
            </button>
          )}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map(p => (
            <button key={p.id} onClick={() => navigate(`/prestations/${p.id}`)}
              className="w-full text-left bg-card border border-border/50 rounded-xl p-4 hover:border-border hover:shadow-sm transition-all group"
              data-testid={`prestation-row-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground">{p.numero}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[p.statut] || "bg-secondary text-muted-foreground"}`}>
                      {STATUT_LABELS[p.statut] || p.statut}
                    </span>
                    {p.niveauUrgence && p.niveauUrgence !== "normal" && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCE_COLORS[p.niveauUrgence] || ""}`}>
                        {p.niveauUrgence === "critique" ? "🔴 Critique" : "🟠 Urgent"}
                      </span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">{p.typePrestation}</span>
                  </div>
                  <p className="font-medium text-foreground text-sm truncate">{p.titre}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    {p.siteNom && <span>{p.siteNom}</span>}
                    {p.clientFinalNom && <span>{p.clientFinalNom}</span>}
                    {p.modeleRobot && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{p.modeleRobot}</span>}
                    {p.dateIntervention && <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400"><Calendar className="w-3 h-3" />Intervention : {formatDate(p.dateIntervention)}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(p.createdAt)}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
